import type { CSSProperties, ReactElement } from "react";
import { LayoutGrid, User } from "lucide-react";
import { Stack, Text } from "@ux/primitives";
import { palette, radius, space } from "@ux/tokens";
import type { AssetAssignTarget } from "./types";

export interface TargetKindToggleProps {
    readonly value: AssetAssignTarget["kind"];
    readonly onChange: (kind: AssetAssignTarget["kind"]) => void;
    readonly disabled?: boolean;
}

/**
 * Two-segment toggle for picking "Workspace" vs "User" as the target of
 * an asset assignment. Inlined here (rather than promoted to
 * @ux/primitives) because both callers — the AssetForm inline picker and
 * the AssignTargetDialog — live in admin/assets and share identical
 * needs. If a third caller ever wants segmented choice, this is the
 * shape to hoist.
 */
export function TargetKindToggle({
    value,
    onChange,
    disabled = false,
}: TargetKindToggleProps): ReactElement {
    return (
        <Stack gap={1}>
            <Text role="eyebrow">Target</Text>
            <div
                role="radiogroup"
                aria-label="Assignment target"
                style={{
                    display: "inline-flex",
                    border: `1px solid ${palette.border}`,
                    borderRadius: radius.md,
                    padding: 2,
                    background: palette.surface,
                    width: "fit-content",
                }}
            >
                <ToggleSegment
                    selected={value === "Workspace"}
                    disabled={disabled}
                    icon={<LayoutGrid size={14} />}
                    label="Workspace"
                    onClick={() => onChange("Workspace")}
                />
                <ToggleSegment
                    selected={value === "User"}
                    disabled={disabled}
                    icon={<User size={14} />}
                    label="User"
                    onClick={() => onChange("User")}
                />
            </div>
        </Stack>
    );
}

interface ToggleSegmentProps {
    readonly selected: boolean;
    readonly disabled: boolean;
    readonly icon: ReactElement;
    readonly label: string;
    readonly onClick: () => void;
}

function ToggleSegment({
    selected,
    disabled,
    icon,
    label,
    onClick,
}: ToggleSegmentProps): ReactElement {
    const style: CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        gap: space[2],
        padding: `${space[1]} ${space[3]}`,
        background: selected ? palette.accentBg : "transparent",
        color: selected ? palette.accentFg : palette.fg,
        border: "none",
        borderRadius: radius.sm,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        font: "inherit",
        fontSize: "0.85rem",
        fontWeight: selected ? 600 : 400,
        transition: "background 80ms ease",
    };
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={onClick}
            style={style}
        >
            {icon}
            {label}
        </button>
    );
}
